import { useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { orderedAppBreakpoints } from '../constants/breakpoints';
import { selectIsMobile } from '../selectors/app';
import breakpoints from '../styles/breakpoints';

type Select<T> = (breakpoint: number) => T;

let mediaQueryLists: [number, MediaQueryList][] | undefined;

function getMediaQueryLists() {
  mediaQueryLists ??= orderedAppBreakpoints
    .filter((breakpoint) => breakpoint > breakpoints.MIN)
    .map((breakpoint) => [breakpoint, window.matchMedia(`(min-width: ${breakpoint}px)`)]);

  return mediaQueryLists;
}

/** The largest app breakpoint the viewport currently reaches. Reads the viewport, so client only. */
export function getBreakpoint() {
  return getMediaQueryLists().find(([, list]) => list.matches)?.[0] ?? breakpoints.MIN;
}

// One listener per media query notifies every subscriber in a single pass. The browser runs
// pending React work between separate listeners, so per-component listeners would commit once
// per subscriber, and once more for every breakpoint a single resize crosses.
const subscribers = new Set<() => void>();
let notifiedBreakpoint: number | undefined;

function handleChange() {
  const breakpoint = getBreakpoint();

  if (breakpoint !== notifiedBreakpoint) {
    notifiedBreakpoint = breakpoint;
    subscribers.forEach((notify) => notify());
  }
}

function subscribe(notify: () => void) {
  if (!subscribers.size) {
    notifiedBreakpoint = getBreakpoint();

    for (const [, list] of getMediaQueryLists()) {
      list.addEventListener('change', handleChange);
    }
  }

  subscribers.add(notify);

  return () => {
    subscribers.delete(notify);

    if (!subscribers.size) {
      for (const [, list] of getMediaQueryLists()) {
        list.removeEventListener('change', handleChange);
      }
    }
  };
}

const identity: Select<number> = (breakpoint) => breakpoint;

/**
 * Reads the viewport's app breakpoint and re-renders when it changes. Pass a `select` function
 * such as `isLarge` to re-render only when its result changes; it must return a primitive.
 *
 * The server can't measure the viewport, so it renders with a guess from the user agent.
 * Hydration renders with the same guess, and React then re-renders any component whose selected
 * value differs on the real viewport. Pass `serverValue` to render that instead of the guess
 * until the client has measured.
 */
export function useBreakpoint(): number;
export function useBreakpoint<T>(select: Select<T>, serverValue?: T): T;
export function useBreakpoint(select: Select<unknown> = identity, serverValue?: unknown) {
  const isMobile = useSelector(selectIsMobile);

  return useSyncExternalStore(
    subscribe,
    () => select(getBreakpoint()),
    () => serverValue ?? select(isMobile ? breakpoints.MIN : breakpoints.LARGE),
  );
}
